import { Component, inject, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiResponseStats, UserStats } from 'src/app/core/api/wager-api';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { PlayerStatsService } from 'src/app/core/services/player-stats/player-stats.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameId } from 'src/app/model/game/game-id';
import { PlayerStatsViewModal } from 'src/app/shared/types/stats';
import { safeModalDismiss } from 'src/app/shared/utils/modal.utils';
import { calcIcon, calcIconColor } from 'src/app/shared/utils/stats.utils';
import { DropdownItem } from 'src/app/ui/drop-down-button/drop-down-button.component';

@Component({
  selector: 'app-player-stats-drawer',
  templateUrl: './player-stats-drawer.component.html',
  styleUrls: ['./player-stats-drawer.component.scss'],
})
export class PlayerStatsDrawerComponent implements OnInit {
  private modalController = inject(ModalController);
  private playerStatsService = inject(PlayerStatsService);
  private themeService = inject(ThemeService);
  private analyticsService = inject(AnalyticsService);

  OperatorService = OperatorService;

  @Input() userId!: string;
  @Input() gameId!: GameId;
  @Input() nickname!: string;

  currentGame: GameId = this.gameId;
  nickName = '...';
  pills: { label: string }[] = [];
  userStats!: UserStats;
  statsList: PlayerStatsViewModal[] = [];
  iconForGlobalRank = 'medal';
  colorForGlobalRank = '--white';
  subtitle!: string;
  unlockedGlobalRank = false;
  drawerConfig = this.themeService.drawerConfig();

  gameOptions: DropdownItem[] = [
    { id: GameId.Blackjack, label: 'Blackjack' },
    { id: GameId.Blocks, label: 'Blocks' },
    { id: GameId.Solitaire, label: 'Solitaire' },
  ];

  ngOnInit() {
    this.analyticsService.log(EventName.StatsDrawerViewed);

    if (this.gameId) this.currentGame = this.gameId;
    if (!this.currentGame) this.currentGame = GameId.Solitaire;
    this.subtitle = '...';
    this.pills = this.playerStatsService.getInitialPillsData();
    this.statsList = this.playerStatsService.getInitialStatsListData();
    this.updateUserGameStatsData();
  }

  onGameChange(selectedId: string): void {
    if (this.currentGame === selectedId) return;
    this.currentGame = selectedId as GameId;
    this.updateUserGameStatsData();
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
  }

  private updatePills(): void {
    const pillsDetails: { label: string; value: number }[] = [
      { label: 'days', value: this.userStats.activeDays ?? 0 },
      // { label: 'matches', value: this.userStats.completedRounds?.value ?? 0 },
      { label: 'wins', value: this.userStats.wonRounds?.value ?? 0 },
    ];

    this.pills = pillsDetails.map((item) => ({
      label: `${this.playerStatsService.getTranslation(item.label)} ${item.value}`,
    }));
  }

  private async getGameStats(userId: string, gameId: GameId): Promise<ApiResponseStats> {
    const job = this.playerStatsService.getGameStats(userId, gameId);
    job.showNoInternetRetry().showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    return await job.run();
  }

  private updateStatsList(): void {
    if (!this.userStats) return;

    this.statsList = this.playerStatsService.transformStatsList([
      {
        label: 'best-score',
        score: this.userStats?.bestScore?.value,
        percentage: this.userStats?.bestScore.top,
      },
      {
        label: 'avg-score',
        score: this.userStats?.averageScore?.value,
        percentage: this.userStats?.averageScore?.top,
      },
      // {
      //   label: 'win-rate',
      //   score: this.userStats?.winRate?.value,
      //   percentage: this.userStats?.winRate?.top,
      //   isRate: true,
      // },
      // {
      //   label: 'completed-rounds',
      //   score: this.userStats?.completedRounds?.value,
      //   percentage: this.userStats?.completedRounds?.top,
      // },
      {
        label: 'won-rounds',
        score: this.userStats?.wonRounds?.value,
        percentage: this.userStats?.wonRounds?.top,
      },
      // {
      //   label: 'drawn-rounds',
      //   score: this.userStats?.drawnRounds?.value,
      //   percentage: this.userStats?.drawnRounds?.top,
      // },
      {
        label: 'best-win-streak',
        score: this.userStats?.bestWinStreak?.value,
        percentage: this.userStats?.bestWinStreak?.top,
      },
      {
        label: 'won-amount',
        score: this.userStats?.wonAmount?.value,
        percentage: this.userStats?.wonAmount?.top,
        isAmount: true,
      },
    ]);
  }

  private async updateUserGameStatsData() {
    const response = await this.getGameStats(this.userId, this.currentGame);
    if (response?.data) {
      this.userStats = response.data;
      this.nickName = response.data.nick;
      this.unlockedGlobalRank = response.data.completedRounds.value >= 5;
      this.subtitle = this.unlockedGlobalRank
        ? this.playerStatsService.getTranslation('ranked-in-top', {
            globalRank: response.data.globalRank,
          })
        : this.playerStatsService.getTranslation('play-matches-global-rank');
      this.iconForGlobalRank = calcIcon(response.data.globalRank);
      this.colorForGlobalRank = calcIconColor(response.data.globalRank);
      this.updatePills();
      this.updateStatsList();
    }
  }
}
